import useAuth, { AuthContextType, User } from '@contexts/useAuth/useAuth';
import { Avatar, Menu, UnstyledButton, useMantineTheme } from '@mantine/core';
import { IconLogout } from '@tabler/icons-react';
import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './AccountMenu.module.css';
import { useNavbarSelector } from '@domains/navbar';
import { PreferencesSection } from './PreferencesSection';
import { useAccountMenuSections } from './useAccountMenuSections';

const ICON_COLOR = 'var(--mantine-color-primary-7)';


const getUserInitials = ( user: User ): string => {
  if ( !user || !user.firstName ) return '';

  const nameParts = [ user.firstName, user.lastName || '' ];
  if ( nameParts.length === 1 ) {
    return nameParts[0].charAt( 0 ).toUpperCase();
  }

  return `${nameParts[0].charAt( 0 )}${nameParts[nameParts.length - 1].charAt( 0 )}`.toUpperCase();
};

type AccountMenuProps = {
  handleAppLogin: () => void;
};

export function AccountMenu({ handleAppLogin }: AccountMenuProps ) {
  const { user, signout } = useAuth() as AuthContextType;
  const { t } = useTranslation( 'account' );
  const [ popoverOffset, setPopoverOffset ] = useState({ crossAxis: 0, mainAxis: 0 });
  const [ logoError, setLogoError ] = useState( false );
  // Controlled so the Preferences section can close the menu after starting a tour
  // (nested submenu items are portalled, so their clicks never bubble up to close it).
  const [ isMenuOpened, setIsMenuOpened ] = useState( false );
  const buttonRef = useRef<HTMLButtonElement>( null );
  const initials = user ? getUserInitials( user ) : '';
  const theme = useMantineTheme();

  // Get business data from navbar domain state
  const businessName = useNavbarSelector( s => s.account.name );
  const accountData = useNavbarSelector( s => s.account.data );
  // TODO: Add businessImage field to AccountsReadmodel when available
  const businessImage = ( accountData as any )?.businessImage || null;

  // Determine what to show: image > name > nothing (avatar only)
  const showLogo = businessImage && !logoError;
  const showName = !showLogo && !!businessName;
  const showPill = showLogo || showName;

  // Entries, labels and feature gates are shared with the mobile drawer in
  // UserNavbar/index.tsx — see useAccountMenuSections (TV1-2294).
  const sections = useAccountMenuSections({ handleAppLogin });

  useEffect(() => {
      const calculateOffset = () => {
        if ( buttonRef.current ) {
          const buttonRect = buttonRef.current.getBoundingClientRect();
          const distanceToRightEdge = window.innerWidth - buttonRect.right;
          setPopoverOffset({ crossAxis: distanceToRightEdge, mainAxis: 10 });
        }
      };

      calculateOffset();
      window.addEventListener( 'resize', calculateOffset );

      return () => {
        window.removeEventListener( 'resize', calculateOffset );
      };
    }, []);

  return (
    <Menu position="bottom-end" offset={popoverOffset} withArrow opened={isMenuOpened} onChange={setIsMenuOpened} >
      {/* id="tour-tours" — the initial tour points its tutorials step at this trigger
          now that the standalone TourMenu button is gone (TV1-2299). */}
      <Menu.Target>
        <UnstyledButton id="tour-tours" ref={buttonRef} className={showPill ? classes.userButton : classes.userButtonAvatarOnly}>
          {/* Company branding - logo or name (only show if we have data) */}
          {showPill && (
            <div className={classes.companyBranding}>
              {showLogo ? (
                <img
                  src={businessImage}
                  alt={businessName || ''}
                  className={classes.companyLogo}
                  onError={() => setLogoError( true )}
                />
              ) : (
                <span className={classes.companyName}>
                  {businessName}
                </span>
              )}
            </div>
          )}
          {/* Avatar with user initials */}
          <div className={classes.avatarWrapper}>
            <Avatar
              size={22}
              radius="xl"
              color="primary"
              styles={{
                root: { backgroundColor: 'var(--mantine-color-primary-9)' },
                placeholder: { fontWeight: 800, color: theme.white }
              }}
            >
              {initials}
            </Avatar>
          </div>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        {sections.map(( section ) => (
          <Fragment key={section.key}>
            {section.showLabel && <Menu.Label>{section.label}</Menu.Label>}
            {section.entries.map(( entry ) => {
              const EntryIcon = entry.icon;
              return (
                <Menu.Item
                  key={entry.key}
                  py='sm'
                  leftSection={<EntryIcon color={ICON_COLOR} size={14} />}
                  onClick={entry.onClick}
                >
                  {entry.label}
                </Menu.Item>
              );
            })}
          </Fragment>
        ))}
        <PreferencesSection onCloseMenu={() => setIsMenuOpened( false )} />
        <Menu.Divider />
        <Menu.Item
          py='sm'
          leftSection={<IconLogout color={'var(--mantine-color-primary-7)'} size={14} stroke={3} />}
          onClick={() => signout()}
        >
          {t( 'log_out', 'Cerrar Sesión' )}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
}
